<script setup lang="ts">
import { computed } from 'vue';
import { AxiosError } from 'axios';
import { useQueryClient, type InfiniteData } from '@tanstack/vue-query';
import StatusChip from '@/components/DataTable/StatusChip.vue';
import { QUIET_BUTTON_CLASS } from '@/components/DataTable/quietButton';
import {
    historySourceLabel,
    historyStateChip,
    historyTimeCell,
    type HistoryTimeCell,
    type HistoryTimeColumn,
} from '@/components/DataTable/lzpCommandHistory';
import type { StatusTone } from '@/components/DataTable/statusChips';
import {
    playerCommandHistoryPagesQueryKey,
    usePlayerCommandHistoryPages,
} from '@/composables/api/useSchedulerSyncApi';
import type { CommandHistoryEntry, CommandHistoryPage } from '@/utils/syncStatus';

/**
 * The player inspector's Command History tab: every command tabasco sent to
 * this player, newest first, and what became of it. It lives inside its own
 * TabsContent, which unmounts while another tab shows, so its query only runs
 * while the tab is showing. Showing the tab again reloads it once it is more
 * than 30 seconds old.
 */
const props = defineProps<{ locationZonePlayerId: number | null }>();

const historyQuery = usePlayerCommandHistoryPages(() => props.locationZonePlayerId);
const queryClient = useQueryClient();

const TIME_COLUMNS: { column: HistoryTimeColumn; label: string }[] = [
    { column: 'issued', label: 'Issued' },
    { column: 'acknowledged', label: 'Acknowledged' },
    { column: 'delivered', label: 'Delivered' },
    { column: 'done', label: 'Done' },
];

/** One table row with every cell already worked out, so each rule runs once per row. */
interface HistoryDisplayRow {
    entry: CommandHistoryEntry;
    commandTitle: string;
    source: { text: string; title: string | null };
    stateChip: { word: string; tone: StatusTone };
    timeCells: { column: HistoryTimeColumn; cell: HistoryTimeCell }[];
}

function commandTitle(entry: CommandHistoryEntry): string {
    return entry.command_truncated
        ? `${entry.command}… Only the start of this command is stored.`
        : entry.command;
}

const displayRows = computed<HistoryDisplayRow[]>(() =>
    (historyQuery.data.value?.pages.flatMap((page) => page.data) ?? []).map((entry) => ({
        entry,
        commandTitle: commandTitle(entry),
        source: historySourceLabel(entry),
        stateChip: historyStateChip(entry.state),
        timeCells: TIME_COLUMNS.map(({ column }) => ({ column, cell: historyTimeCell(entry, column) })),
    }))
);

// True once a first page has arrived, even one with no rows.
const hasLoadedFirstPage = computed(() => historyQuery.data.value !== undefined);
// A next-page failure has its own message beside Load more, so only a failed
// first load or refresh shows the panel's error.
const hasLoadError = computed(() => historyQuery.isError.value && !historyQuery.isFetchNextPageError.value);
// A 403 replaces everything, even rows an earlier load showed.
const isRefused = computed(() => {
    const error = historyQuery.error.value;
    return hasLoadError.value && error instanceof AxiosError && error.response?.status === 403;
});
// An empty page that still has a cursor is not the end, so Load more shows instead.
const showsEmptyMessage = computed(
    () =>
        hasLoadedFirstPage.value &&
        displayRows.value.length === 0 &&
        !historyQuery.hasNextPage.value &&
        !hasLoadError.value
);

/**
 * Reloads from the first page only. Trimming the cached pages first makes the
 * refetch one request with no cursor, and `refetch` cancels a Load more that is
 * still running, so its page never comes back. The rows stay shown meanwhile.
 */
function refreshFromFirstPage(): void {
    queryClient.setQueryData<InfiniteData<CommandHistoryPage, string | null>>(
        playerCommandHistoryPagesQueryKey(props.locationZonePlayerId),
        (cached) => cached && { pages: cached.pages.slice(0, 1), pageParams: cached.pageParams.slice(0, 1) }
    );
    void historyQuery.refetch();
}

function loadMore(): void {
    if (historyQuery.hasNextPage.value && !historyQuery.isFetching.value) {
        void historyQuery.fetchNextPage();
    }
}
</script>

<template>
    <section
        class="border-border/80 bg-muted/20 rounded-lg border px-3 py-2"
        data-testid="lzp-inspector-command-history"
    >
        <div class="flex items-center justify-between gap-2">
            <h4 class="text-sm font-semibold">Command history</h4>
            <button
                type="button"
                :class="QUIET_BUTTON_CLASS"
                data-testid="lzp-history-refresh"
                :disabled="locationZonePlayerId === null"
                @click="refreshFromFirstPage"
            >
                Refresh
            </button>
        </div>

        <p v-if="historyQuery.isPending.value" class="text-muted-foreground mt-1 text-xs" data-testid="lzp-history-loading">
            Loading command history…
        </p>

        <template v-else>
            <p v-if="isRefused" class="text-muted-foreground mt-1 text-xs" data-testid="lzp-history-refused">
                You don't have permission to see this player's command history.
            </p>
            <p v-else-if="hasLoadError" class="text-destructive mt-1 flex items-center gap-2 text-xs" data-testid="lzp-history-error">
                Couldn't load command history.
                <button
                    type="button"
                    :class="QUIET_BUTTON_CLASS"
                    data-testid="lzp-history-retry"
                    :disabled="historyQuery.isFetching.value"
                    @click="refreshFromFirstPage"
                >
                    Retry
                </button>
            </p>

            <p
                v-if="showsEmptyMessage"
                class="text-muted-foreground mt-1 text-xs"
                data-testid="lzp-history-empty"
            >
                No commands have been sent to this player yet.
            </p>

            <!-- Seven columns do not fit the side peek, so the table scrolls sideways there. -->
            <div v-if="displayRows.length > 0 && !isRefused" class="mt-2 overflow-x-auto">
                <table class="w-full min-w-[48rem] table-fixed text-left text-xs">
                    <colgroup>
                        <col class="w-[24%]" />
                        <col class="w-[12%]" />
                        <col class="w-[16%]" />
                        <col v-for="time in TIME_COLUMNS" :key="time.column" class="w-[12%]" />
                    </colgroup>
                    <thead class="text-muted-foreground">
                        <tr>
                            <th class="py-1 pr-2 font-medium">Command</th>
                            <th class="py-1 pr-2 font-medium">Source</th>
                            <th class="py-1 pr-2 font-medium">State</th>
                            <th v-for="time in TIME_COLUMNS" :key="time.column" class="py-1 pr-2 font-medium">
                                {{ time.label }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="row in displayRows"
                            :key="row.entry.command_id"
                            class="border-border/60 border-t align-top"
                            data-testid="lzp-history-row"
                            :data-command-id="row.entry.command_id"
                        >
                            <td class="py-1 pr-2" :title="row.commandTitle" data-testid="lzp-history-command">
                                <span class="flex min-w-0 items-baseline gap-1">
                                    <span class="truncate font-mono">{{ row.entry.command }}</span>
                                    <span v-if="row.entry.command_truncated" class="text-muted-foreground shrink-0">(cut off)</span>
                                </span>
                            </td>
                            <td
                                class="truncate py-1 pr-2"
                                :title="row.source.title ?? undefined"
                                data-testid="lzp-history-source"
                            >
                                {{ row.source.text }}
                            </td>
                            <td class="py-1 pr-2" data-testid="lzp-history-state">
                                <StatusChip :word="row.stateChip.word" :tone="row.stateChip.tone" />
                                <span
                                    v-if="row.entry.state === 'skipped' && row.entry.skipped_reason"
                                    class="text-muted-foreground ml-1"
                                >{{ row.entry.skipped_reason }}</span>
                            </td>
                            <td
                                v-for="{ column, cell } in row.timeCells"
                                :key="column"
                                class="py-1 pr-2 tabular-nums"
                                :data-testid="`lzp-history-time-${column}`"
                                :data-time-state="cell.kind"
                            >
                                <time v-if="cell.kind === 'time'" :title="cell.full">{{ cell.short }}</time>
                                <span v-else-if="cell.kind === 'waiting'" class="italic text-amber-600 dark:text-amber-400">waiting</span>
                                <span v-else-if="cell.kind === 'na'" class="text-muted-foreground">n/a</span>
                                <span v-else class="text-muted-foreground" :title="cell.title">—</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-if="historyQuery.hasNextPage.value && !isRefused" class="mt-1 flex items-center gap-2">
                <button
                    type="button"
                    :class="QUIET_BUTTON_CLASS"
                    data-testid="lzp-history-load-more"
                    :disabled="historyQuery.isFetching.value"
                    @click="loadMore"
                >
                    Load more
                </button>
                <span
                    v-if="historyQuery.isFetchNextPageError.value"
                    class="text-destructive text-xs"
                    data-testid="lzp-history-load-more-error"
                >Couldn't load more.</span>
            </div>
        </template>
    </section>
</template>
